import type { ApiError, ChatEvent } from "@navibiz/chat-core";

/** A failed Console API call, carrying the gateway's typed error. */
export class ConsoleApiError extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
  ) {
    super(error.message);
  }
}

const BASE = "/admin/v1";
const CSRF = { "X-Requested-With": "navibiz-console" };

/** Fired on 401 so the app shell can drop back to the login screen. */
export const SESSION_EXPIRED = "navibiz-console:session-expired";

async function handle<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  if (response.ok) return (await response.json()) as T;
  const body = (await response.json().catch(() => null)) as { error?: ApiError } | null;
  const error = new ConsoleApiError(response.status, body?.error ?? { code: "internal_error", message: `HTTP ${response.status}` });
  if (response.status === 401 && !response.url.endsWith("/auth/login")) window.dispatchEvent(new Event(SESSION_EXPIRED));
  throw error;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => fetch(`${BASE}${path}`, { credentials: "same-origin", signal }).then(handle<T>),
  post: <T>(path: string, body?: unknown) =>
    fetch(`${BASE}${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { ...CSRF, "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }).then(handle<T>),
  patch: <T>(path: string, body: unknown) =>
    fetch(`${BASE}${path}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { ...CSRF, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle<T>),
  put: <T>(path: string, body: unknown) =>
    fetch(`${BASE}${path}`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { ...CSRF, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle<T>),
  delete: <T>(path: string) => fetch(`${BASE}${path}`, { method: "DELETE", credentials: "same-origin", headers: CSRF }).then(handle<T>),
  /** DELETE carrying a confirmation body (permanent project deletion). */
  deleteWith: <T>(path: string, body: unknown) =>
    fetch(`${BASE}${path}`, {
      method: "DELETE",
      credentials: "same-origin",
      headers: { ...CSRF, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle<T>),
  /** PUT a file as the raw request body; its type travels as Content-Type (avatar upload). */
  upload: <T>(path: string, file: Blob) =>
    fetch(`${BASE}${path}`, { method: "PUT", credentials: "same-origin", headers: { ...CSRF, "Content-Type": file.type }, body: file }).then(handle<T>),

  /** POST that answers with Server-Sent Events (playground, test runs). */
  async *stream<E = ChatEvent>(path: string, body: unknown, signal?: AbortSignal): AsyncGenerator<E> {
    const response = await fetch(`${BASE}${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { ...CSRF, "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
    if (!response.ok || !response.body) await handle(response);
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary: number;
      while ((boundary = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, boundary).replace(/^data:\s?/gm, "");
        buffer = buffer.slice(boundary + 2);
        if (frame.trim()) yield JSON.parse(frame) as E;
      }
    }
  },
};
